import { FC } from 'react';
import { IAttachment } from '../notes/NotesClient.ts';
import { QuillEditor } from '../components/editor/QuillEditor.tsx';
import { Card } from '../components/card/Card.tsx';
import { Field } from '../components/field/Field.tsx';
import { TextInput } from '../components/field/TextInput.tsx';
import { DateInput } from '../components/field/DateInput.tsx';
import { SegmentButton, SegmentInput } from '../components/field/SegmentInput.tsx';
import { TimeInput } from '../components/field/TimeInput.tsx';
import { AttachmentArea } from '../notes/AttachmentArea.tsx';
import { FieldLabel } from '../components/field/FieldLabel.tsx';
import { IAircraftNote, IAircraftNoteType } from './IAircraftNote.ts';

export interface IAircraftLogEntryFormProps {
    note: IAircraftNote;
    noteChanged: (updatedNote: IAircraftNote, noteLabel?: string) => void;
    attachmentAdded?: (attachment: IAttachment) => void;
    attachmentRemoved?: (attachment: IAttachment) => void;
}

export const AircraftLogEntryForm: FC<IAircraftLogEntryFormProps> = ({
    note,
    noteChanged,
    attachmentAdded,
    attachmentRemoved
}) => {
    const onNoteTitleChanged = (title: string) => {
        noteChanged({...note, title: title || '' });
    };

    const onNoteDateChanged = (dateTime?: string) => {
        dateTime = dateTime || new Date().toISOString();
        if (note.otherAttributes.dateTime) {
            const datePart = dateTime.substring(0, 10);
            const timePart = note.otherAttributes.dateTime.substring(10);
            dateTime = datePart + timePart;
        }
        noteChanged({...note, otherAttributes: { ...note.otherAttributes, dateTime }});
    };

    const onNoteTimeChanged = (dateTime: string) => {
        if (note.otherAttributes.dateTime) {
            const datePart = note.otherAttributes.dateTime.substring(0, 10);
            const timePart = dateTime.substring(10);
            dateTime = datePart + timePart;
        }
        noteChanged({...note, otherAttributes: { ...note.otherAttributes, dateTime }});
    };

    const onNoteTypeChanged = (type: string) => {
        noteChanged({...note, otherAttributes: { ...note.otherAttributes, type: type as IAircraftNoteType || 'other' }});
    };

    const onNoteHtmlChanged = (html: string) => {
        noteChanged({...note, html });
    };

    const onUpload = (uploadedFiles: IAttachment[]) => {
        noteChanged({
            ...note,
            attachments: [ ...note.attachments, ...uploadedFiles ]
        });
        if (attachmentAdded) {
            for (const a of uploadedFiles) {
                attachmentAdded(a);
            }
        }
    };

    const onAttachmentDeleted = (deleted: IAttachment) => {
        noteChanged({
            ...note,
            attachments: note.attachments.filter(a => a.url !== deleted.url)
        });
        if (attachmentRemoved) {
            attachmentRemoved(deleted);
        }
    };

    return (
        <div data-type="note-form" data-note-id={note.noteId} style={{height: '100%', display: 'flex', flexDirection: 'column'}}>
            <Field label="Title">
                <TextInput
                    value={note.title}
                    onChange={onNoteTitleChanged}
                    autofocus={!note.title}
                />
            </Field>

            <div style={{display: 'flex'}}>
                <Field label="Date">
                    <DateInput value={note.otherAttributes.dateTime} onChange={onNoteDateChanged} />
                </Field>
                <Field label="Time">
                    <TimeInput value={note.otherAttributes.dateTime} onChange={onNoteTimeChanged} />
                </Field>
            </div>

            <Field label="Type" readonly>
                <SegmentInput value={note.otherAttributes.type} onChange={onNoteTypeChanged}>
                    <SegmentButton value="maintenance">Maintenance</SegmentButton>
                    <SegmentButton value="incident">Incident</SegmentButton>
                    <SegmentButton value="other">Other<br/></SegmentButton>
                </SegmentInput>
            </Field>

            <div style={{margin: '0 20px'}}>
                <FieldLabel>Notes</FieldLabel>
                <div style={{margin: '-8px -8px 0 -12px'}}>
                    <Card>
                        <QuillEditor
                            html={note.html}
                            onChange={html => onNoteHtmlChanged(html)}
                            placeholder="Start writing here…"
                            height="250px"
                            autofocus={Boolean(note.title && !note.html)}
                        />
                    </Card>
                </div>
            </div>

            <AttachmentArea
                attachments={note.attachments}
                onUpload={onUpload}
                onDelete={onAttachmentDeleted}
            />
        </div>
    );
};
